import { Tab, Tabs, Spinner } from "@heroui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, Outlet, useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "@wc/domain/authn";
import { useOrg } from "@wc/domain/organizations";

import TeamProvider from "../contexts/team-context";
import useTeam from "../hooks/use-team";

/**
 * @returns {React.ReactElement} Inside the provider so we can get team information
 */
function TeamSettingsLayoutInternal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const teamData = useTeam();
  const orgData = useOrg();

  const validTabKeys = ["overview", "info", "members"];
  const currentPath = location.pathname.split("/").pop();
  const defaultSelectedKey = validTabKeys.includes(currentPath)
    ? currentPath
    : "overview";

  if (!teamData.team) {
    return (
      <div className="mt-8 flex justify-center">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-4 text-4xl font-bold tracking-tight">
        {t("team.settingsTitle")}{" "}
        <span className="text-primary">{teamData?.team.name}</span>
      </h1>
      <Tabs
        variant="bordered"
        color="primary"
        selectedKey={defaultSelectedKey}
        onSelectionChange={(key) =>
          navigate(`/orgs/${orgData?.org.id}/teams/${teamData?.team.id}/${key}`)
        }
      >
        {validTabKeys.map((tk) => {
          return <Tab key={tk} title={t(`team.tab-${tk}`)} />;
        })}
      </Tabs>
      <div className="mt-4">
        <Outlet />
      </div>
    </div>
  );
}

/**
 * This is a layout that should always be used in the context of a team.
 * This expects there to be a teamId in the url parameters.
 * @returns {React.ReactElement} The layout for the team settings pages
 */
export function TeamSettingsLayout() {
  const { teamId } = useParams();
  const authData = useAuth();

  return (
    <TeamProvider teamId={teamId} userId={authData.getCurrentUser().id}>
      <TeamSettingsLayoutInternal />
    </TeamProvider>
  );
}
